// Development Inventory skill library — loaded once from GET /api/dev/skill-library (backed by the CSV in backend/data).
// Only Skill IDs are ever stored on player records; all names/questions/feedback text come from here.
import { useEffect, useState } from "react";
import axios from "axios";

let ITEMS = [];
let META = { version: "", synced_at: null, active: 0 };
let loader = null;
export const SKILL_BY_ID = {}; // live map, filled after load (active + retired)

export function loadSkillLibrary() {
  if (!loader) {
    loader = axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dev/skill-library`).then(r => {
      ITEMS = r.data.skills || [];
      META = { version: r.data.version || "", synced_at: r.data.synced_at || null, active: r.data.active || 0 };
      Object.keys(SKILL_BY_ID).forEach(k => delete SKILL_BY_ID[k]);
      ITEMS.forEach(s => { SKILL_BY_ID[s.id] = s; });
      return ITEMS;
    }).catch(e => { loader = null; throw e; });
  }
  return loader;
}

export function useSkillLibrary() {
  const [state, setState] = useState({ ready: ITEMS.length > 0, meta: META, error: null });
  useEffect(() => {
    let live = true;
    loadSkillLibrary().then(() => live && setState({ ready: true, meta: META, error: null }))
      .catch(e => live && setState({ ready: false, meta: META, error: e?.response?.data?.detail || "Could not load the skill library" }));
    return () => { live = false; };
  }, []);
  return state;
}

export const PILLAR_ORDER = ["physical", "character", "technical", "tactical"];
export const PILLARS = {
  physical: { title: "Physical Tools", step: 1 },
  character: { title: "Character DNA", step: 1 },
  technical: { title: "Technical Skill", step: 2 },
  tactical: { title: "Tactical Skill", step: 2 },
};

const EMPTY = { looks: "", build: "" };
// Rating on a Skill ID that is no longer in the library at all: keep it visible, never guess.
export const legacySkill = (id) => ({ id, pillar: null, category: "Legacy", skill: id, status: "legacy", categoryQuestion: "", anchorSummary: "", limiterSummary: "", whatItIs: "", whyItMatters: "", anchor: EMPTY, good: EMPTY, limiter: EMPTY });
export const getSkill = (id, snapshot) => snapshot?.[id] || SKILL_BY_ID[id] || legacySkill(id);

// Tactical play-type categories → Current Role Play Type IDs (mirrors backend skill_library.CATEGORY_PLAY_TYPES).
export const CATEGORY_PLAY_TYPES = { "PnR ball handler": "PT-01", "PnR roll / pop man": "PT-02", "Spot up": "PT-03", "Handoffs": "PT-04", "Off-ball screens": "PT-04", "Isolation": "PT-05", "Post up": "PT-06", "Cutter": "PT-07", "Transition": "PT-08", "Offensive rebounds": "PT-09" };
// Skill names shared across categories (Scoring, Passing, Shot selection…) get their category outside the category list.
const sharedName = (s) => ITEMS.some(o => o.id !== s.id && o.skill === s.skill && o.pillar === s.pillar);
// Display name; shared names read "Spot up: Scoring"; retired skills read "Off hand (retired: now Driving ability / control)".
export const skillLabel = (s) => {
  if (!s) return "";
  const base = sharedName(s) ? `${s.category}: ${s.skill}` : s.skill;
  return s.status === "retired" ? `${base} (retired${s.mergedInto && SKILL_BY_ID[s.mergedInto] ? `: now ${SKILL_BY_ID[s.mergedInto].skill}` : ""})` : base;
};
export const activeSkills = () => ITEMS.filter(s => s.status === "active");
export const totalItems = () => activeSkills().length;
export const skillsFor = (pillar) => activeSkills().filter(s => s.pillar === pillar);
// Active items in file order. Retired skills are hidden from the current assessment (their old ratings stay in the record).
export const skillsForAssessment = (pillar) => skillsFor(pillar);
export const isLive = (r) => getSkill(r.skillId).status !== "retired";
export const categoriesFor = (pillar, ratings) => [...new Set(skillsForAssessment(pillar, ratings).map(s => s.category))];
export const categoryQuestion = (pillar, category) => ITEMS.find(s => s.pillar === pillar && s.category === category)?.categoryQuestion || "";

export const RATING_META = {
  anchor: { label: "Anchor", color: "#10b981", bg: "bg-[#065f46]", text: "text-[#4ade80]" },
  good: { label: "Good", color: "#3b82f6", bg: "bg-[#1e3a8a]", text: "text-[#60a5fa]" },
  limiter: { label: "Rate Limiter", color: "#f87171", bg: "bg-[#7f1d1d]", text: "text-[#f87171]" },
};
export const RATER_ROLES = [["coach", "Coach"], ["trainer", "Trainer"], ["parent", "Parent"], ["player", "Player"]];
// Legacy alias used by Step 2 film/stats UI.
export const COMPLETED_BY_OPTIONS = RATER_ROLES;

// Character DNA limiter text is written "Daily: … Episode: …" — return just the requested half.
export const limiterLooks = (skill, limiterType) => {
  const txt = skill.limiter.looks || "";
  const m = txt.match(/Daily:\s*([\s\S]*?)\s*Episode:\s*([\s\S]*)/i);
  if (!m) return txt;
  if (limiterType === "daily") return m[1].trim();
  if (limiterType === "episode") return m[2].trim();
  return txt;
};

export const ratingsMap = (ratings) => Object.fromEntries((ratings || []).map(r => [r.skillId, r]));
export const pillarCounts = (ratings, pillar) => {
  const mine = (ratings || []).filter(r => getSkill(r.skillId).pillar === pillar && isLive(r));   // retired skills are excluded from every count
  return { total: skillsFor(pillar).length, rated: mine.length, anchor: mine.filter(r => r.rating === "anchor").length, good: mine.filter(r => r.rating === "good").length, limiter: mine.filter(r => r.rating === "limiter").length };
};
